import { Redirect, Switch } from 'react-router'

import { ProtectedRoute } from '@webapp/app/components'
import { useFeatureFlag } from '@webapp/hooks'
import { useAppRoutes } from '@webapp/hooks/useAppRoutes'
import {
  OverallSpendReportPage,
  OverallSpendReportPageTabs,
} from '@webapp/modules/accounts-payable/pages/OverallSpendReportPage'
import { ReceivingSummaryPage } from '@webapp/modules/purchasing/pages/ReceivingSummaryPage'
import {
  ExpensesBySecondaryCategoryReportDetailsPage,
  ExpensesBySecondaryCategoryReportPage,
  OrdersBySecondaryCategoryReportDetailsPage,
  OrdersBySecondaryCategoryReportPage,
  OrdersByUserReportDetailsPage,
  OrdersByUserReportPage,
  OrdersByVendorReportDetailsPage,
  OrdersByVendorReportPage,
  ReportExportHistoryPage,
} from '@webapp/modules/reports'
import { Permissions } from '@webapp/platform/iam'

const REPORT_DETAILS_ID_PARAM = ':id(\\d+)'

export const ReportsRouter = () => {
  const { url, brp } = useAppRoutes()

  const PSERV_346_BASE_MIGRATION = useFeatureFlag('PSERV_346_BASE_MIGRATION')

  return (
    <Switch>
      {/* PSERV_346: flag-off falls back to the Angular report pages. Each
          redirect must precede its React route below so it wins in the Switch
          when the flag is off. */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('ReceivingSummaryPage')}
          to='/#/ap/reports/receiving'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('OrdersByVendorReportDetailsPage', {
            id: REPORT_DETAILS_ID_PARAM,
          })}
          to='/#/reports/orders/vendor/:id'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('OrdersByVendorReportPage')}
          to='/#/reports/orders/vendor'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('OrdersByUserReportDetailsPage', {
            id: REPORT_DETAILS_ID_PARAM,
          })}
          to='/#/reports/orders/user/:id'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('OrdersByUserReportPage')}
          to='/#/reports/orders/user'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('OrdersBySecondaryCategoryReportDetailsPage', {
            id: REPORT_DETAILS_ID_PARAM,
          })}
          to='/#/reports/orders/department/:id'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('OrdersBySecondaryCategoryReportPage')}
          to='/#/reports/orders/department'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('ExpensesBySecondaryCategoryReportDetailsPage', {
            id: REPORT_DETAILS_ID_PARAM,
          })}
          to='/#/reports/expenses/department/:id'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('ExpensesBySecondaryCategoryReportPage')}
          to='/#/reports/expenses/department'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('ReportExportHistoryPage')}
          to='/#/reports/export-history'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('OverallSpendReportPage', {
            type: ':type(department|account-code)',
          })}
          to='/#/ap/reports/spend-tracker/:type'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('OverallSpendReportPage', { type: ':type?' })}
          to='/#/ap/reports/spend-tracker/'
        />
      )}

      {/* Flag-on React report pages. ReceivingSummaryPage gates via the
          flag-off redirect above (its customFilter would swallow a
          featureFlags prop). */}
      <ProtectedRoute
        exact
        path={brp('ReceivingSummaryPage')}
        component={ReceivingSummaryPage}
        customFilter={({ permissions }) =>
          permissions.includes(Permissions.REPORTS_VIEW_RECEIVING_SUMMARY) &&
          permissions.includes(Permissions.LEGACY_PAYMENT)
        }
      />
      <ProtectedRoute
        exact
        path={brp('OrdersByVendorReportPage')}
        component={OrdersByVendorReportPage}
        permissions={[Permissions.REPORTS_VIEW_ORDERS_BY_VENDOR]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        path={brp('OrdersByVendorReportDetailsPage', {
          id: REPORT_DETAILS_ID_PARAM,
        })}
        component={OrdersByVendorReportDetailsPage}
        permissions={[Permissions.REPORTS_VIEW_ORDERS_BY_VENDOR]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        exact
        path={brp('OrdersByUserReportPage')}
        component={OrdersByUserReportPage}
        permissions={[Permissions.REPORTS_VIEW_ORDERS_BY_USER]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        path={brp('OrdersByUserReportDetailsPage', {
          id: REPORT_DETAILS_ID_PARAM,
        })}
        component={OrdersByUserReportDetailsPage}
        permissions={[Permissions.REPORTS_VIEW_ORDERS_BY_USER]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        exact
        path={brp('OrdersBySecondaryCategoryReportPage')}
        component={OrdersBySecondaryCategoryReportPage}
        permissions={[Permissions.REPORTS_VIEW_ORDERS_BY_DEPARTMENT]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        path={brp('OrdersBySecondaryCategoryReportDetailsPage', {
          id: REPORT_DETAILS_ID_PARAM,
        })}
        component={OrdersBySecondaryCategoryReportDetailsPage}
        permissions={[Permissions.REPORTS_VIEW_ORDERS_BY_DEPARTMENT]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        exact
        path={brp('ExpensesBySecondaryCategoryReportPage')}
        component={ExpensesBySecondaryCategoryReportPage}
        permissions={[Permissions.REPORTS_VIEW_EXPENSES_BY_DEPARTMENT]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <ProtectedRoute
        path={brp('ExpensesBySecondaryCategoryReportDetailsPage', {
          id: REPORT_DETAILS_ID_PARAM,
        })}
        component={ExpensesBySecondaryCategoryReportDetailsPage}
        permissions={[Permissions.REPORTS_VIEW_EXPENSES_BY_DEPARTMENT]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      {/* No own permission — reached from any accessible report. */}
      <ProtectedRoute
        exact
        path={brp('ReportExportHistoryPage')}
        component={ReportExportHistoryPage}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />

      <ProtectedRoute
        exact
        path={brp('OverallSpendReportPage', {
          type: ':type(department|account-code)',
        })}
        component={OverallSpendReportPage}
        permissions={[Permissions.REPORTS_VIEW_SPEND_TRACKER]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      {/* Empty or unknown :type falls back to the department tab. */}
      <Redirect
        from={brp('OverallSpendReportPage', { type: ':type?' })}
        to={url('OverallSpendReportPage', {
          type: OverallSpendReportPageTabs.DEPARTMENT,
        })}
      />

      <Redirect to={url('DashboardPage')} />
    </Switch>
  )
}
